import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { emailMonitorAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface BlacklistCheck {
  id: number;
  domain: string;
  ip_address: string | null;
  blacklist_provider: string;
  is_listed: boolean;
  listing_reason: string | null;
  checked_at: string;
}

const EmailBlacklistPage: React.FC = () => {
  useDocumentTitle('Blacklist Status');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState<Record<string, BlacklistCheck[]>>({});

  const fetchBlacklist = useCallback(async () => {
    if (!currentAsset) return;
    try {
      setLoading(true);
      const response = await emailMonitorAPI.getBlacklistStatus(currentAsset.id);
      if (response.data.status === 'success') {
        setGrouped(response.data.data.grouped || {});
      }
    } catch (error) {
      console.error('Failed to fetch blacklist status:', error);
      toast.error('Failed to load blacklist status');
    } finally {
      setLoading(false);
    }
  }, [currentAsset]);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!currentAsset) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="bi bi-shield-x" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
            <p className="mt-3 text-muted">Please select an asset to view blacklist status</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <p className="mt-3 text-muted">Checking blacklist status...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const allDomains = Object.keys(grouped);

  if (allDomains.length === 0) {
    return (
      <DashboardLayout>
        <div className="container-fluid p-0">
          <h4 className="mb-1 fw-bold">Blacklist Status</h4>
          <p className="text-muted mb-4">Check if your domain is listed on email blacklists</p>
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-search" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
              <h5 className="mt-3">No Blacklist Data</h5>
              <p className="text-muted">Run a scan from the Email Security Dashboard to check blacklist status.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid p-0">
        <div className="mb-4">
          <h4 className="mb-1 fw-bold">Blacklist Status</h4>
          <p className="text-muted mb-0">Monitor if your domain or mail server IP is listed on email blacklists</p>
        </div>

        {allDomains.map((domain) => {
          const checks = grouped[domain];
          const listedCount = checks.filter((c) => c.is_listed).length;
          const ipAddress = checks[0]?.ip_address;
          const lastChecked = checks[0]?.checked_at;

          return (
            <div key={domain} className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0 fw-bold">
                      <i className="bi bi-globe me-2"></i>{domain}
                    </h6>
                    <small className="text-muted">
                      IP: {ipAddress || 'Unknown'} | Last checked: {formatDate(lastChecked)}
                    </small>
                  </div>
                  <div>
                    {listedCount === 0 ? (
                      <span className="badge bg-success-subtle text-success px-3 py-2">
                        <i className="bi bi-check-circle me-1"></i>All Clear
                      </span>
                    ) : (
                      <span className="badge bg-danger-subtle text-danger px-3 py-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>{listedCount} Listing{listedCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px' }}>Status</th>
                        <th>Blacklist Provider</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checks.map((check) => (
                        <tr key={check.id}>
                          <td className="text-center">
                            {check.is_listed ? (
                              <i className="bi bi-x-circle-fill text-danger"></i>
                            ) : (
                              <i className="bi bi-check-circle-fill text-success"></i>
                            )}
                          </td>
                          <td>
                            <span className="fw-medium">{check.blacklist_provider}</span>
                          </td>
                          <td>
                            {check.is_listed ? (
                              <span className="text-danger">
                                <small>{check.listing_reason || 'Listed'}</small>
                              </span>
                            ) : (
                              <span className="text-success"><small>Clean</small></span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}

        {/* Info Card */}
        <div className="card border-0 shadow-sm bg-light">
          <div className="card-body">
            <h6 className="fw-bold"><i className="bi bi-info-circle me-2"></i>About Email Blacklists</h6>
            <p className="text-muted mb-2">
              Email blacklists (DNSBLs) are real-time databases of IP addresses and domains known to send spam. Being listed can cause your emails to be rejected or sent to spam folders.
            </p>
            <p className="text-muted mb-0">
              <strong>If listed:</strong> Contact the blacklist provider for delisting instructions. Ensure your email server is properly configured and not being used for spam. Review your SPF, DKIM, and DMARC records on the DNS Records page.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmailBlacklistPage;
